import React, { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import { concepts } from '../../data/researchDemo';
import type { Claim, Concept } from '../../types/researchDemo';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { focusRing, glassPanel } from '../../utils/demoUi';
import { conceptById, predicateLabel } from '../../utils/researchDemo';
type Point = {
  x: number;
  y: number;
};
const NODE_W = 190;
const NODE_H = 56;
export function KnowledgeMap() {
  const {
    state,
    openClaim
  } = useResearchDemo();
  const approved = useMemo(() => state.claims.filter(claim => claim.status === 'approved'), [state.claims]);
  const edges = useMemo(() => layoutEdges(approved), [approved]);
  const connectedIds = new Set(approved.flatMap(claim => [claim.subjectId, claim.objectId]));
  const unconnected = concepts.filter(concept => !connectedIds.has(concept.id));
  return <div data-id="element-2091" data-mp="30inypdw7ci9g">
      <DemoSectionHeader title="Knowledge Map" description="Controlled concepts and the relationships curators have approved. Proposed, rejected and revision-requested records stay outside this view." data-id="element-2092" data-mp="1iasg25rw2kmy" />

      <p className="mb-6 rounded-lg border-l-4 border-teal bg-teal-pale px-4 py-3 text-sm leading-relaxed text-ink-dark" data-id="element-2093" data-mp="18kh35tq8913x">
        A connection represents a reviewed interpretation of the cited evidence. It does not automatically establish causality.
      </p>

      <div className={`${glassPanel} overflow-x-auto p-4`} data-id="element-2094" data-mp="zhxyr56smgfk">
        <svg viewBox="0 0 760 480" className="h-auto w-full min-w-[640px]" role="img" aria-label={`Knowledge map with ${concepts.length} concepts and ${approved.length} approved relationships. The list below provides the same information.`} data-id="element-2095" data-mp="ukg0c8qejjlt">
          <defs data-id="element-2096" data-mp="2jz4cq9yzt1ju">
            <marker id="map-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse" data-id="element-2097" data-mp="2jzm5uxqcyznj">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#6366C9" data-id="element-2098" data-mp="dbvk6zihesc1" />
            </marker>
          </defs>
          {edges.map(({
          claim,
          start,
          end
        }) => {
          const mid = {
            x: (start.x + end.x) / 2,
            y: (start.y + end.y) / 2
          };
          const label = predicateLabel(claim.predicate);
          const width = label.length * 6.6 + 18;
          return <g key={claim.id} className="cursor-pointer" onClick={() => openClaim(claim.id)} aria-hidden="true" data-id="element-2099" data-mp="8dbv4q8ld0fg">
                <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke="#6366C9" strokeWidth={2} markerEnd="url(#map-arrow)" data-id="element-2100" data-mp="w0a9nid5hyaq" />
                <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke="transparent" strokeWidth={18} data-id="element-2101" data-mp="n1ykzlvuhakx" />
                <rect x={mid.x - width / 2} y={mid.y - 12} width={width} height={24} rx={12} fill="#F2F2FC" stroke="#6366C9" strokeOpacity={0.35} data-id="element-2102" data-mp="s09ufvi89j2m" />
                <text x={mid.x} y={mid.y + 4} textAnchor="middle" fontSize={12} fontWeight={600} fill="#4446A8" data-id="element-2103" data-mp="37qmdgya30ngl">
                  {label}
                </text>
              </g>;
        })}
          {concepts.map(concept => <ConceptNode key={concept.id} concept={concept} connected={connectedIds.has(concept.id)} data-id="element-2104" data-mp="308oehnpj0urm" />)}
        </svg>
        <p className="mt-2 px-2 text-xs text-ink-muted" data-id="element-2105" data-mp="3e74kfcfkeh8p">Select a connection to open its evidence record. Dashed concepts have no approved connection yet.</p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]" data-id="element-2106" data-mp="2lj8qgfhdr3fm">
        <section aria-labelledby="relationship-list" data-id="element-2107" data-mp="2gkp78mu685qy">
          <h2 id="relationship-list" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2108" data-mp="2gm8mqb806cro">
            Approved relationships ({approved.length})
          </h2>
          {approved.length === 0 ? <p className="mt-3 text-sm text-ink-muted" data-id="element-2109" data-mp="2qhacn9a3rivb">No relationships are currently approved.</p> : <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-2110" data-mp="22qo2ncgjfoio">
              {approved.map(claim => <li key={claim.id} className="py-3" data-id="element-2111" data-mp="112stpdjiqtqg">
                  <button type="button" onClick={() => openClaim(claim.id)} className={`group flex w-full items-start justify-between gap-4 text-left ${focusRing}`} data-id="element-2112" data-mp="1143v56phrpee">
                    <span data-id="element-2113" data-mp="1etpb087zvoi7">
                      <span className="block font-semibold text-ink-dark" data-id="element-2114" data-mp="1euz9gyireypd">
                        {conceptById(claim.subjectId)?.label} <span className="text-lavender" data-id="element-2115" data-mp="1js8zafa29cdq">{predicateLabel(claim.predicate)}</span>{' '}
                        {conceptById(claim.objectId)?.label}
                      </span>
                      <span className="mt-0.5 block text-sm text-ink-muted" data-id="element-2116" data-mp="1tsjbbwkd0drb">{claim.interpretation}</span>
                      <span className="mt-1 block text-xs text-ink-muted" data-id="element-2117" data-mp="1xrif3ducu4h6">
                        {claim.id} · v{claim.version} · {claim.sourceId} {claim.passageId}
                      </span>
                    </span>
                    <span className="mt-1 inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-navy group-hover:text-navy-light" data-id="element-2118" data-mp="3f26w7i00g9o">
                      Evidence
                      <ArrowRight size={14} aria-hidden="true" data-id="element-2119" data-mp="3n95hzibfclh8" />
                    </span>
                  </button>
                </li>)}
            </ul>}
        </section>

        <section aria-labelledby="vocabulary-list" data-id="element-2120" data-mp="3eak0lqhovci1">
          <h2 id="vocabulary-list" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2121" data-mp="eu5dojytmrpp">
            Vocabulary entries
          </h2>
          <p className="mt-1 text-sm text-ink-muted" data-id="element-2122" data-mp="9w3p5jimm06u">
            {unconnected.length === 0 ? 'Every concept has at least one approved connection.' : `${unconnected.length} concepts have no approved connection and remain in the controlled vocabulary.`}
          </p>
          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-2123" data-mp="2qkpoxc93vtzd">
            {concepts.map(concept => <li key={concept.id} className={`rounded-md border px-2.5 py-1 text-xs ${connectedIds.has(concept.id) ? 'border-line bg-white text-ink-dark' : 'border-dashed border-line text-ink-muted'}`} data-id="element-2124" data-mp="2ljjvfc7mg2os">
                <span className="font-semibold" data-id="element-2125" data-mp="2e1u33bslij2k">{concept.label}</span> · {concept.role}
                <span className="sr-only" data-id="element-2126" data-mp="2i2e3co8gu2sx">{connectedIds.has(concept.id) ? ', connected' : ', no approved connection'}</span>
              </li>)}
          </ul>
        </section>
      </div>
    </div>;
}
function ConceptNode({
  concept,
  connected
}: {
  concept: Concept;
  connected: boolean;
}) {
  return <g aria-hidden="true" data-id="element-2127" data-mp="1udut435uvfy7">
      <rect x={concept.x - NODE_W / 2} y={concept.y - NODE_H / 2} width={NODE_W} height={NODE_H} rx={12} fill={connected ? '#FFFFFF' : '#F9FBFC'} stroke={connected ? '#245B8F' : '#B8C4D2'} strokeWidth={1.5} strokeDasharray={connected ? undefined : '5 4'} data-id="element-2128" data-mp="1ueo1t9odkfiz" />
      <text x={concept.x} y={concept.y - 3} textAnchor="middle" fontSize={14} fontWeight={700} fill={connected ? '#1F2A37' : '#5B6675'} data-id="element-2129" data-mp="1qdayp7co2qfr">
        {concept.label}
      </text>
      <text x={concept.x} y={concept.y + 15} textAnchor="middle" fontSize={11} fill="#5B6675" data-id="element-2130" data-mp="11sfvcid1sx1v">
        {concept.role}
      </text>
    </g>;
}

// Positions each approved edge between node borders, offsetting parallel edges.
function layoutEdges(claims: Claim[]) {
  const groups = new Map<string, Claim[]>();
  claims.forEach(claim => {
    const key = [claim.subjectId, claim.objectId].sort().join('|');
    groups.set(key, [...(groups.get(key) ?? []), claim]);
  });
  return claims.flatMap(claim => {
    const from = conceptById(claim.subjectId);
    const to = conceptById(claim.objectId);
    if (!from || !to) return [];
    const siblings = groups.get([claim.subjectId, claim.objectId].sort().join('|')) ?? [claim];
    const index = siblings.indexOf(claim);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    const shift = (index - (siblings.length - 1) / 2) * 32;
    const offset = {
      x: -dy / length * shift,
      y: dx / length * shift
    };
    const a = {
      x: from.x + offset.x,
      y: from.y + offset.y
    };
    const b = {
      x: to.x + offset.x,
      y: to.y + offset.y
    };
    const start = clipToNode(a, b);
    const endRaw = clipToNode(b, a);
    const end = {
      x: endRaw.x - dx / length * 4,
      y: endRaw.y - dy / length * 4
    };
    return [{
      claim,
      start,
      end
    }];
  });
}
function clipToNode(center: Point, toward: Point): Point {
  const dx = toward.x - center.x;
  const dy = toward.y - center.y;
  if (dx === 0 && dy === 0) return center;
  const tx = dx === 0 ? Infinity : NODE_W / 2 / Math.abs(dx);
  const ty = dy === 0 ? Infinity : NODE_H / 2 / Math.abs(dy);
  const t = Math.min(tx, ty);
  return {
    x: center.x + dx * t,
    y: center.y + dy * t
  };
}